import CorporateLife from "components/workers/CorporateLife";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { getFormattedDate } from "utils/functions";
import { useState, useEffect, useContext } from "react";
import InfoContext from "pages/InfoContext";
import ProfessionalReviews from "components/workers/ProfessionalReviews";

const DashboardStaff = ({ workers }) => {
  const {
    lang,
    userScope,
    missionCreatedLateList,
    missionClosedLateList,
    missionClosedInTimeList,
    filteredCorporateLife,
    userBySlug,
    staffFourYearsReview,
    downloadReviewFile,
  } = useContext(InfoContext);

  const [lateCreatedMissions, setLateCreatedMissions] = useState([]);
  const [lateClosedMissions, setLateClosedMissions] = useState([]);
  const [lateClosedMissionsDays, setLateClosedMissionsDays] = useState([]);
  const header = ({ isStart, inAdvance }) => [
    {
      label: {
        key: "consultant",
        value: (
          <FormattedMessage
            id="staff_worker"
            defaultMessage="Consultant/Sous-traitant"
          />
        ),
      },
      icon: "user",
      width: "22%",
    },
    {
      label: {
        key: "client",
        value: <FormattedMessage id="client" defaultMessage="Client" />,
      },
      icon: "user",
      width: "24%",
    },
    {
      label: {
        key: isStart ? "mission-start" : "mission-end",
        value: isStart ? (
          <FormattedMessage
            id="mission_start"
            defaultMessage="Début de mission"
          />
        ) : (
          <FormattedMessage
            id="end_of_mission"
            defaultMessage="Fin de mission"
          />
        ),
      },
      icon: "calendar",
      width: "15%",
    },
    {
      label: {
        key: isStart ? "mission-creation-date" : "mission-end-update",
        value: isStart ? (
          <FormattedMessage
            id="mission_creation"
            defaultMessage="Création de la mission"
          />
        ) : (
          <FormattedMessage
            id="end_assignment_date"
            defaultMessage="Date de saisie de fin de mission"
          />
        ),
      },
      icon: "calendar",
      width: "20%",
    },
    {
      label: {
        key: inAdvance ? "number-of-days-advance" : "number-of-days-late",
        value: inAdvance ? (
          <FormattedMessage
            id="number_of_days_in_advance"
            defaultMessage="Nombre de jours en avance"
          />
        ) : (
          <FormattedMessage
            id="number_of_days_overdue"
            defaultMessage="Nombre de jours de retard"
          />
        ),
      },
      icon: "calendar",
      width: "19%",
    },
  ];

  const formatMissionData = ({ missions, dateFields, table }) => {
    const lastColumnContentHelper = (elm) => {
      if (table === "closedInTimeMissionsTable") {
        return elm?.["closed_earlier_days"];
      } else {
        return elm?.[
          dateFields.includes("mission_closed_at")
            ? "closed_late_days"
            : "created_late_days"
        ];
      }
    };

    return missions?.map((elm) => ({
      consultant: (
        <Link
          to={`/${userScope}/${elm?.worker_type}/${elm?.worker_slug}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {elm?.worker_full_name}
        </Link>
      ),
      client_id: (
        <Link
          to={`/${userScope}/client/${elm?.client_slug}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {elm?.client_name}
        </Link>
      ),
      ...Object.fromEntries(
        dateFields.map((field) => [field, getFormattedDate(elm?.[field], lang)])
      ),
      lastColumn: lastColumnContentHelper(elm),
    }));
  };

  useEffect(() => {
    if (missionCreatedLateList?.length > 0) {
      setLateCreatedMissions(
        formatMissionData({
          missions: missionCreatedLateList,
          dateFields: ["mission_start_date", "mission_created_at"],
          table: "createdLateMissionsTable",
        })
      );
    } else setLateCreatedMissions([]);
  }, [missionCreatedLateList, lang]);

  useEffect(() => {
    if (missionClosedLateList?.length > 0) {
      setLateClosedMissions(
        formatMissionData({
          missions: missionClosedLateList,
          dateFields: ["mission_end_date", "mission_closed_at"],
          table: "closedLateMissionsTable",
        })
      );
    } else setLateClosedMissions([]);
  }, [missionClosedLateList, workers, lang]);

  useEffect(() => {
    if (missionClosedInTimeList?.length > 0) {
      setLateClosedMissionsDays(
        formatMissionData({
          missions: missionClosedInTimeList,
          dateFields: ["mission_start_date", "mission_created_at"],
          table: "closedInTimeMissionsTable",
        })
      );
    } else setLateClosedMissionsDays([]);
  }, [missionClosedInTimeList, workers, lang]);

  return (
    <>
      {staffFourYearsReview?.length > 0 && (
        <ProfessionalReviews
          person={userBySlug}
          reviews={staffFourYearsReview}
          downloadReview={downloadReviewFile}
        />
      )}
      <CorporateLife filteredCorporateLife={filteredCorporateLife} />
      <div className="section">
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={
            <FormattedMessage
              id="late_created_missions"
              defaultMessage="Missions crées en retard (sur les 12 derniers mois)"
            />
          }
        />
        <div>
          <Table
            id="created_missions"
            header={header({ isStart: true })}
            content={lateCreatedMissions}
          />
        </div>
      </div>
      <div className="section">
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={
            <FormattedMessage
              id="late_closed_missions"
              defaultMessage="Missions cloturées en retard (sur les 12 derniers mois)"
            />
          }
        />
        <div>
          <Table
            id="closed_missions"
            header={header({ isStart: false })}
            content={lateClosedMissions}
          />
        </div>
      </div>
      <div className="section">
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={
            <FormattedMessage
              id="late_closed_missions_15_days"
              defaultMessage="Missions cloturées < 15 jours avant la FDM  (sur les 12 derniers mois)"
            />
          }
        />
        <div>
          <Table
            id="late_closed_missions_15_days"
            header={header({ isStart: false, inAdvance: true })}
            content={lateClosedMissionsDays}
          />
        </div>
      </div>
    </>
  );
};

DashboardStaff.propTypes = {
  workers: PropTypes.array,
};

export default DashboardStaff;
